'use client';

import clsx from 'clsx';
import { cloneElement, useState, useMemo } from 'react';
import { Modal, ChevronDown, Input, useDisclosure } from '@ywwwtseng/react-kit';
import { Typography } from '@ywwwtseng/tma-react-kit';
import { Search, X } from 'lucide-react';
import { InViewport } from '../common/InViewport';

export function Select({
  searchable = false,
  disabled = false,
  action,
  searchRightAdornment,
  trigger,
  contentTop,
  contentBottom,
  contentTitle,
  label,
  placeholder,
  value,
  options,
  onChange,
  onScrollEnd,
}: {
  searchable?: boolean;
  disabled?: boolean;
  action?: React.ReactNode;
  searchRightAdornment?: React.ReactNode;
  trigger?: React.ReactElement<{ onClick: () => void }>;
  contentTop?: React.ReactNode;
  contentBottom?: React.ReactNode;
  contentTitle?: React.ReactNode;
  label?: string;
  placeholder?: string;
  value?: string | null;
  options: {
    name: string;
    key: string;
    icon?: React.ReactNode;
    description?: React.ReactNode | string | null;
  }[];
  onChange: (value: string) => void;
  onScrollEnd?: () => void;
}) {
  const [search, setSearch] = useState('');
  const { isOpen, onClose, onOpen } = useDisclosure();

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    return options.filter(
      (option) =>
        option.name.toLowerCase().includes(search.toLowerCase()) ||
        (typeof option.description === 'string' ? option.description.toLowerCase().includes(search.toLowerCase()) : false)
    );
  }, [options, search]);

  const selected = useMemo(() => {
    return options.find((option) => option.key === value);
  }, [options, value]);

  return (
    <>
      {cloneElement(
        trigger || (
          <div
            className={clsx(
              'w-full flex items-center justify-between gap-1 pl-2 pr-3 bg-form-element rounded-lg text-sm h-10',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <div className="flex items-center gap-2">
              {selected?.icon && (
                <div className="scale-75">{selected.icon}</div>
              )}
              <Typography
                size="2"
                className={value ? 'text-foreground' : 'text-placeholder'}
              >
                {selected?.name ?? placeholder}
              </Typography>
            </div>
            <ChevronDown width={20} height={20} />
          </div>
        ),
        { onClick: () => !disabled && onOpen() }
      )}
      <Modal
        classes={{ content: 'border-1 border-white/10 max-h-[80vh]' }}
        title={label ?? ''}
        open={isOpen}
        onClose={onClose}
      >
        {label && <Typography size="1">{label}</Typography>}
        {searchable && (
          <div className="w-full px-4 flex items-center justify-center gap-2">
            <div className="w-full flex items-center justify-between gap-2 bg-[#202020] rounded-full text-sm h-10 pl-2 pr-1.5">
              <Search width={20} height={20} />
              <Input
                className="flex-1"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {searchRightAdornment}
              {!searchRightAdornment && search && (
                <div
                  className="cursor-pointer rounded-full p-1"
                  onClick={() => setSearch('')}
                >
                  <X width={16} height={16} />
                </div>
              )}
            </div>
            {action && (
              <div
                className="flex items-center min-w-content"
                onClick={() => onClose()}
              >
                {action}
              </div>
            )}
          </div>
        )}
        {contentTop && <div className="w-full flex items-center justify-start px-4">{contentTop}</div>}
        {contentTitle && <div className="w-full flex items-center justify-start px-4"><Typography size="1">{contentTitle}</Typography></div>}
        <div className="flex-1 flex-col justify-center items-center w-full gap-4 pb-4 px-4 overflow-y-auto">
          {filteredOptions.map((option) => (
            <div
              className={clsx(
                'flex items-center w-full min-h-12 rounded-lg py-1 px-2',
                option.icon ? 'gap-2 justify-between' : 'justify-center'
              )}
              key={option.key}
              onClick={() => {
                onChange(option.key);
                onClose();
              }}
            >
              {option.icon}
              <div className="flex flex-col items-end gap-1">
                <Typography size="1" weight={500}>
                  {option.name}
                </Typography>
                {option.description && (
                  <Typography size="1" color="#777777">
                    {option.description}
                  </Typography>
                )}
              </div>
            </div>
          ))}
          {filteredOptions.length > 0 && (
            <InViewport onInView={onScrollEnd} />
          )}
          {options.length > 0 && filteredOptions.length === 0 && (
            <div className="flex items-center justify-center w-full h-full">
              <Typography size="1" color="#777777">
                No results found
              </Typography>
            </div>
          )}
        </div>
        {contentBottom && <div className="w-full flex items-center justify-start px-4">{contentBottom}</div>}
      </Modal>
    </>
  );
}
